import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const StatCard = ({
  label, value, color, icon, onClick
}) => (
  <div
    onClick={onClick}
    style={{
      background:'var(--surface)',
      border:'1px solid var(--border)',
      borderRadius:'12px',
      padding:'20px',
      position:'relative',
      overflow:'hidden',
      borderTop:`2px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
      transition:'all 0.15s',
    }}
    onMouseEnter={e => {
      if (onClick)
        e.currentTarget.style.borderColor =
          color;
    }}
    onMouseLeave={e => {
      if (onClick)
        e.currentTarget.style.borderColor =
          'var(--border)';
    }}
  >
    <div style={{
      fontSize:'11px',
      fontFamily:'DM Mono,monospace',
      color:'var(--text3)',
      letterSpacing:'1px',
      textTransform:'uppercase',
      marginBottom:'10px',
    }}>
      {label}
    </div>
    <div style={{
      fontFamily:'Syne,sans-serif',
      fontSize:'36px',
      fontWeight:'800',
      color: color,
      lineHeight:1,
    }}>
      {value ?? 0}
    </div>
    <div style={{
      position:'absolute',
      right:'16px', top:'16px',
      fontSize:'28px', opacity:0.1,
    }}>
      {icon}
    </div>
  </div>
);

const VendorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await reportsAPI.getDashboard();
      setData(res.data.data);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{
      padding:'40px',
      color:'var(--text3)',
      fontFamily:'DM Mono,monospace',
    }}>
      Loading dashboard...
    </div>
  );

  const stats = data?.stats || {};
  const activity = data?.recent_activity || [];
  const weekly = data?.weekly_deliveries || [];

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Welcome back, {user?.name}
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            {user?.business_name} —
            Operations Overview
          </p>
        </div>
        <button
          onClick={() => navigate('/tickets')}
          style={{
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor:'pointer',
          }}
        >
          + New Ticket
        </button>
      </div>

      {/* Stats Row 1 */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(4,1fr)',
        gap:'16px',
        marginBottom:'16px',
      }}>
        <StatCard
          label="Open Tickets"
          value={stats.open_tickets}
          color="var(--accent)"
          icon="◈"
          onClick={() => navigate('/tickets')}
        />
        <StatCard
          label="In Progress"
          value={stats.in_progress}
          color="var(--yellow)"
          icon="▷"
          onClick={() => navigate('/tickets')}
        />
        <StatCard
          label="Delivered Today"
          value={stats.delivered_today}
          color="var(--green)"
          icon="✓"
        />
        <StatCard
          label="SLA Breaches"
          value={stats.sla_breaches}
          color="var(--red)"
          icon="⚠"
        />
      </div>

      {/* Stats Row 2 */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(4,1fr)',
        gap:'16px',
        marginBottom:'24px',
      }}>
        <StatCard
          label="Active Engineers"
          value={stats.active_engineers}
          color="var(--purple)"
          icon="◉"
          onClick={() => navigate('/engineers')}
        />
        <StatCard
          label="Total Engineers"
          value={stats.total_engineers}
          color="var(--text2)"
          icon="◉"
          onClick={() => navigate('/engineers')}
        />
        <StatCard
          label="Total Customers"
          value={stats.total_customers}
          color="var(--accent)"
          icon="◫"
          onClick={() => navigate('/customers')}
        />
        <StatCard
          label="Items In Stock"
          value={stats.items_in_stock}
          color="var(--green)"
          icon="⊞"
          onClick={() => navigate('/inventory')}
        />
      </div>

      {/* Bottom Row */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'2fr 1fr',
        gap:'16px',
      }}>
        {/* Recent Activity */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          <div style={{
            padding:'16px 20px',
            borderBottom:
              '1px solid var(--border)',
            fontFamily:'Syne,sans-serif',
            fontSize:'14px', fontWeight:'700',
          }}>
            Recent Activity
          </div>
          <div style={{ padding:'8px 0' }}>
            {activity.length === 0 ? (
              <div style={{
                padding:'30px',
                textAlign:'center',
                color:'var(--text3)',
                fontSize:'13px',
              }}>
                No recent activity
              </div>
            ) : (
              activity.map((a, i) => (
                <div key={i} style={{
                  display:'flex',
                  gap:'14px',
                  padding:'12px 20px',
                  borderBottom:
                    i < activity.length - 1
                      ? '1px solid var(--border)'
                      : 'none',
                  alignItems:'flex-start',
                }}>
                  <div style={{
                    width:'28px',
                    height:'28px',
                    borderRadius:'50%',
                    background:
                      a.event_type === 'delivered'
                        ? 'rgba(0,229,160,0.15)'
                      : a.event_type === 'created'
                        ? 'rgba(0,212,255,0.15)'
                      : 'rgba(255,192,64,0.15)',
                    color:
                      a.event_type === 'delivered'
                        ? 'var(--green)'
                      : a.event_type === 'created'
                        ? 'var(--accent)'
                      : 'var(--yellow)',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    fontSize:'12px',
                    flexShrink:0,
                  }}>
                    {a.event_type === 'delivered'
                      ? '✓'
                      : a.event_type === 'created'
                      ? '+'
                      : '→'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{
                      fontSize:'13px',
                      fontWeight:'500',
                    }}>
                      {a.description}
                    </div>
                    <div style={{
                      fontSize:'11px',
                      color:'var(--text3)',
                      fontFamily:
                        'DM Mono,monospace',
                      marginTop:'3px',
                      display:'flex',
                      gap:'10px',
                    }}>
                      <span>
                        {a.ticket_number}
                      </span>
                      <span>
                        {a.performed_by_name}
                      </span>
                      <span>
                        {new Date(a.created_at)
                          .toLocaleTimeString(
                            'en-IN',
                            { hour:'2-digit',
                              minute:'2-digit' }
                          )}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Weekly Chart */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          <div style={{
            padding:'16px 20px',
            borderBottom:
              '1px solid var(--border)',
            fontFamily:'Syne,sans-serif',
            fontSize:'14px', fontWeight:'700',
          }}>
            Deliveries This Week
          </div>
          <div style={{
            padding:'20px',
          }}>
            {weekly.length === 0 ? (
              <div style={{
                textAlign:'center',
                color:'var(--text3)',
                fontSize:'13px',
                padding:'20px',
              }}>
                No deliveries this week yet
              </div>
            ) : (
              <div style={{
                display:'flex',
                alignItems:'flex-end',
                gap:'8px',
                height:'120px',
              }}>
                {weekly.map((w, i) => {
                  const max = Math.max(
                    ...weekly.map(x =>
                      parseInt(x.count))
                  );
                  const height =
                    (parseInt(w.count) / max)
                    * 100;
                  return (
                    <div key={i} style={{
                      flex:1,
                      display:'flex',
                      flexDirection:'column',
                      alignItems:'center',
                      gap:'4px',
                      height:'100%',
                      justifyContent:
                        'flex-end',
                    }}>
                      <div style={{
                        fontSize:'10px',
                        fontFamily:
                          'DM Mono,monospace',
                        color:'var(--text2)',
                      }}>
                        {w.count}
                      </div>
                      <div style={{
                        width:'100%',
                        height:`${height}%`,
                        background:
                          'linear-gradient(to top, var(--accent2), var(--accent))',
                        borderRadius:'4px 4px 0 0',
                        minHeight:'4px',
                      }} />
                      <div style={{
                        fontSize:'9px',
                        fontFamily:
                          'DM Mono,monospace',
                        color:'var(--text3)',
                      }}>
                        {w.day}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick Links */}
            <div style={{
              marginTop:'20px',
              display:'flex',
              flexDirection:'column',
              gap:'8px',
            }}>
              {[
                { label:'View All Tickets',
                  path:'/tickets',
                  color:'var(--accent)' },
                { label:'Live Map',
                  path:'/map',
                  color:'var(--green)' },
                { label:'Reports',
                  path:'/reports',
                  color:'var(--purple)' },
              ].map(link => (
                <div
                  key={link.path}
                  onClick={() =>
                    navigate(link.path)}
                  style={{
                    display:'flex',
                    justifyContent:
                      'space-between',
                    alignItems:'center',
                    padding:'10px 14px',
                    background:
                      'var(--surface2)',
                    borderRadius:'8px',
                    cursor:'pointer',
                    fontSize:'13px',
                    fontWeight:'500',
                    color: link.color,
                    transition:'all 0.15s',
                  }}
                >
                  {link.label}
                  <span>→</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;